// Drop Zone v2 — inspiration heuristics (spec #761, §B.2).
//
// The canonical inspiration extension set lives here. It is exactly the set the
// CLI's inspiration upload (`plug-inspiration.ts`) recognises today — the spec
// (§B.2) requires the two surfaces import it from "one shared module" so the
// browser preview and the CLI never drift on what counts as an inspiration.
//
// `INSPIRATION_SOURCE_TYPES` carries the frozen Inspiration API contract's
// `source_type` value for each extension (docs/contracts/inspiration-api.md).
// The CLI imports it verbatim, so this map IS the contract mapping — keep the
// values exact. The detection engine derives its coarser one-line subtype
// (`extToSubtype`) separately, by extension.

import type { DropEntry, DetectionSignal, InspirationSubtype } from '../types.js';

/**
 * Extension → Inspiration API `source_type` (the frozen contract enum). Single
 * source of truth for typing a single dropped file. Mirrors the CLI's historic
 * `detectSourceType` switch exactly; the archive case (`.zip`) is handled by the
 * caller, not here, because it needs unpacking rather than typing.
 */
export const INSPIRATION_SOURCE_TYPES: Readonly<Record<string, string>> = {
  '.html': 'html',
  '.htm': 'html',
  '.md': 'markdown',
  '.pdf': 'pdf',
  '.pptx': 'pptx',
  '.docx': 'docx',
  '.xlsx': 'xlsx',
  '.png': 'image',
  '.jpg': 'image',
  '.jpeg': 'image',
  '.svg': 'image',
  '.webp': 'image',
  '.jsx': 'react_single_file',
  '.tsx': 'react_single_file',
  '.mmd': 'mermaid',
  '.json': 'json',
  '.ts': 'code',
  '.js': 'code',
  '.py': 'code',
  '.go': 'code',
  '.rs': 'code',
  '.sql': 'code',
  '.yaml': 'code',
  '.yml': 'code',
  '.toml': 'code',
  '.mp4': 'video',
  '.webm': 'video',
  '.mov': 'video',
  '.ogv': 'video',
  '.mp3': 'audio',
  '.wav': 'audio',
  '.m4a': 'audio',
  '.ogg': 'audio',
  '.txt': 'text',
  '.log': 'text',
  '.ipynb': 'jupyter',
  '.csv': 'csv',
  '.tsv': 'csv',
};

/**
 * The extension set the detection decision tree uses to decide app-vs-
 * inspiration (spec §B.2). It is the keys of the contract map: a single file
 * with one of these extensions, or a bundle of only such files, is an
 * inspiration unless an app run manifest is also present (the ambiguity rule).
 */
export const INSPIRATION_EXTENSIONS: ReadonlySet<string> = new Set<string>(
  Object.keys(INSPIRATION_SOURCE_TYPES),
);

/** Lowercased extension including the dot, or '' when there is none. */
export function extOf(path: string): string {
  const base = path.slice(path.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  // No dot, or a leading-dot dotfile with no real extension (".gitignore").
  if (dot <= 0) return '';
  return base.slice(dot).toLowerCase();
}

/** Map a single extension to the coarse subtype used in the one-line guess. */
export function extToSubtype(ext: string): InspirationSubtype {
  switch (ext) {
    case '.pdf':
      return 'pdf';
    case '.pptx':
      return 'deck';
    case '.md':
      return 'markdown';
    case '.png':
    case '.jpg':
    case '.jpeg':
    case '.webp':
    case '.svg':
      return 'image';
    case '.mp4':
    case '.mov':
    case '.webm':
    case '.ogv':
      return 'video';
    default:
      return 'doc';
  }
}

export interface InspirationFinding {
  /** True when every weighed file is an inspiration-set file. */
  allInspiration: boolean;
  /** True when there was exactly one weighed file. */
  single: boolean;
  /** The coarse subtype, or `mixed` across differing subtypes. */
  subtype: InspirationSubtype | undefined;
  signals: DetectionSignal[];
}

/**
 * Weigh the inspiration signals over a noise-filtered file list.
 *
 * `files` must already exclude directories and noise (.git, node_modules, …).
 */
export function findInspirationSignals(files: DropEntry[]): InspirationFinding {
  if (files.length === 0) {
    return { allInspiration: false, single: false, subtype: undefined, signals: [] };
  }

  const exts = files.map((f) => extOf(f.path));
  const inSet = exts.map((e) => INSPIRATION_EXTENSIONS.has(e));
  const allInspiration = inSet.every(Boolean);
  const single = files.length === 1;

  const subtypes = new Set<InspirationSubtype>();
  for (let i = 0; i < files.length; i++) {
    if (inSet[i]) subtypes.add(extToSubtype(exts[i] ?? ''));
  }
  const subtype: InspirationSubtype | undefined =
    subtypes.size === 0
      ? undefined
      : subtypes.size === 1
        ? [...subtypes][0]
        : 'mixed';

  const signals: DetectionSignal[] = [];
  if (allInspiration && single) {
    signals.push({
      marker: 'single-inspiration-file',
      weight: 0.95,
      contributes_to: 'inspiration',
    });
  } else if (allInspiration) {
    signals.push({
      marker: 'all-files-inspiration',
      weight: 0.85,
      contributes_to: 'inspiration',
    });
  }

  // Static-page bundle: has an HTML file but isn't a clean all-inspiration set
  // (e.g. html + css assets). Medium confidence — "static page" (spec §B.2).
  if (!allInspiration && exts.some((e) => e === '.html' || e === '.htm')) {
    signals.push({
      marker: 'static-html-bundle',
      weight: 0.5,
      contributes_to: 'inspiration',
    });
  }

  return { allInspiration, single, subtype, signals };
}
